//#region src/utils.ts
function match(key, cases) {
	const result = cases[key];
	if (typeof result === "function") return result(key);
	return result;
}
function takeWhile(arr, predicate, opts = {}) {
	const start = opts.startingFrom ?? 0;
	const result = [];
	for (let i = start; i < arr.length; i++) {
		if (!predicate(arr[i])) break;
		result.push(arr[i]);
	}
	return result;
}
function takeUntil(arr, predicate, opts = {}) {
	const start = opts.startingFrom ?? 0;
	for (let i = start; i < arr.length; i++) if (!predicate(arr[i])) return arr.slice(start, i);
	return arr.slice(start);
}
function outdent(str) {
	let lines = str.split("\n");
	lines = lines.filter((line, index) => index !== 0 && index !== lines.length - 1 || line.trim() !== "");
	let minIndent = Math.min(...lines.map((line) => line === "" ? Infinity : line.match(/^\s*/)?.[0].length ?? 0));
	lines = lines.map((line) => line.slice(minIndent));
	return lines.join("\n");
}
async function sleep(ms) {
	await new Promise((resolve) => setTimeout(resolve, ms));
}
function isTruthy(value) {
	return Boolean(value);
}
function previewJson(json, options = {}) {
	const { maxDepth = 3, wrapLimit = 80, objectKeyLimit } = options;
	const truncateString = (str) => str.length > wrapLimit ? str.substring(0, wrapLimit - 3) + "..." : str;
	const formatValue = (value, depth = 0, indent = "") => {
		if (value === null) return "null";
		if (value === void 0) return "undefined";
		switch (typeof value) {
			case "string": return `"${truncateString(value)}"`;
			case "number":
			case "boolean": return String(value);
			case "object":
				if (depth >= maxDepth) return "[Object]";
				const nextIndent = indent + "	";
				if (Array.isArray(value)) {
					if (value.length === 0) return "[]";
					const items = value.slice(0, objectKeyLimit).map((item) => formatValue(item, depth + 1, nextIndent));
					const shouldWrap = items.join(", ").length > wrapLimit;
					const itemsString = shouldWrap ? items.map((item) => `${nextIndent}${item}`).join(",\n") : items.join(", ");
					const moreItems = value.length > 10 ? `${shouldWrap ? "\n" : ""}${nextIndent}... ${value.length - 10} more items` : "";
					if (shouldWrap) return `[\n${itemsString}${moreItems}\n${indent}]`;
					else return `[${itemsString}${moreItems}]`;
				} else {
					const keys = Object.keys(value);
					if (keys.length === 0) return "{}";
					return `{\n${keys.slice(0, objectKeyLimit).map((key) => `${nextIndent}"${key}": ${formatValue(value[key], depth + 1, nextIndent)}`).join(",\n")}${keys.length > 10 ? `\n${nextIndent}... ${keys.length - 10} more keys` : ""}\n${indent}}`;
				}
			default: return String(value);
		}
	};
	return formatValue(json);
}
function range(start, end) {
	if (end === void 0) return Array.from({ length: start }, (_, i) => i);
	return Array.from({ length: end - start }, (_, i) => start + i);
}
/**
* Creates an array of array values not included in the other given arrays,
* using iteratee to generate the criterion by which they're compared.
*/
function differenceBy(array, values, iteratee) {
	const valueSet = new Set(values.map(iteratee));
	return array.filter((item) => !valueSet.has(iteratee(item)));
}
function reduceRight(array, callback, initialValue) {
	if (array.length === 0 && initialValue === void 0) return;
	let accumulator;
	let startIndex;
	if (initialValue !== void 0) {
		accumulator = initialValue;
		startIndex = array.length - 1;
	} else {
		if (array.length === 0) return;
		accumulator = array[array.length - 1];
		startIndex = array.length - 2;
	}
	for (let i = startIndex; i >= 0; i--) accumulator = callback(accumulator, array[i], i, array);
	return accumulator;
}
/**
* Creates a debounced function that delays invoking func until after wait milliseconds
* have elapsed since the last time the debounced function was invoked.
*/
function debounce(func, wait) {
	let timeoutId;
	let lastArgs;
	let lastThis;
	const debounced = function(...args) {
		lastArgs = args;
		lastThis = this;
		if (timeoutId !== void 0) clearTimeout(timeoutId);
		timeoutId = setTimeout(() => {
			timeoutId = void 0;
			func.apply(lastThis, lastArgs);
		}, wait);
	};
	debounced.cancel = () => {
		if (timeoutId !== void 0) {
			clearTimeout(timeoutId);
			timeoutId = void 0;
		}
	};
	debounced.flush = () => {
		if (timeoutId !== void 0) {
			clearTimeout(timeoutId);
			timeoutId = void 0;
			return func.apply(lastThis, lastArgs);
		}
	};
	return debounced;
}
function groupBy(values, keyFn) {
	const result = {};
	for (const value of values) {
		const key = keyFn(value);
		if (!(key in result)) result[key] = [];
		result[key].push(value);
	}
	return result;
}

//#endregion
export { debounce, differenceBy, groupBy, isTruthy, match, outdent, previewJson, range, reduceRight, sleep, takeUntil, takeWhile };